import React, { useEffect, useState } from 'react';
import { api } from '../shared/api';
import { formatMoney, formatNumber, localIsoDate } from '../shared/formatting';

type Locale = 'hr' | 'en';

type DayRow = {
    key: string;
    qty: number; book_eur: number; fine_g: number; spot_eur: number;
    prev_qty: number | null; prev_book_eur: number | null; prev_fine_g: number | null; prev_spot_eur: number | null;
    delta_qty: number | null; delta_book_eur: number | null; delta_fine_g: number | null; delta_spot_eur: number | null;
    definition_version?: string | null; definition_changed?: boolean;
};

type DayResp = { scope: string; requested: string; date: string | null; prev_date: string | null; rows: DayRow[] };

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * N2 (mail 13.09.): pogled d2d za 40.61 i 40.62. Datepicker bira dan, tablica
 * pokazuje sve retke iz dnevnog snapshota i razliku prema prethodnom snimljenom
 * danu. Izvor je GET /gold-coverage/day (bi_gold_coverage_snapshots).
 */
export function CoverageDayView({ token, locale, scope, labels }: { token: string; locale: Locale; scope: '40.61' | '40.62' | '40.63'; labels: Record<string, string> }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(todayIso());
    const [data, setData] = useState<DayResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        let alive = true;
        setLoading(true); setError(null);
        api<DayResp>(`/gold-coverage/day?scope=${scope}&date=${date}`, token)
            .then((d) => { if (alive) setData(d); })
            .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'API'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open, date, scope, token]);

    const shift = (days: number) => {
        const d = new Date(`${date}T12:00:00`);
        d.setDate(d.getDate() + days);
        setDate(localIsoDate(d));
    };
    const delta = (v: number | null, money: boolean) => {
        if (v === null) return <span className="muted">-</span>;
        const txt = money ? formatMoney(v, locale) : formatNumber(v, locale);
        return <span style={{ fontWeight: v !== 0 ? 600 : 400 }}>{v > 0 ? '+' : ''}{txt}</span>;
    };

    return (
        <div className="card" style={{ border: '1px solid #e2e8f0', padding: '1rem', marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
                    {open ? '▼' : '▶'} {t('Pogled po danu', 'Day view')}
                </h3>
                {open ? (
                    <>
                        <button type="button" onClick={() => shift(-1)}>{t('dan ranije', 'previous day')}</button>
                        <input type="date" value={date} max={todayIso()} onChange={(e) => e.target.value && setDate(e.target.value)} />
                        <button type="button" onClick={() => shift(1)} disabled={date >= todayIso()}>{t('dan kasnije', 'next day')}</button>
                        <button type="button" onClick={() => setDate(todayIso())}>{t('danas', 'today')}</button>
                    </>
                ) : null}
            </div>
            {!open ? null : loading ? (
                <p className="muted">{t('Učitavam…', 'Loading…')}</p>
            ) : error ? (
                <p className="error">{error}</p>
            ) : !data || data.date === null ? (
                <p className="muted">{t('Za taj dan i ranije nema snimljenog stanja.', 'No snapshot on or before that day.')}</p>
            ) : (
                <>
                    <p className="muted" style={{ margin: '0.5rem 0' }}>
                        {data.date !== data.requested ? `${t('Za odabrani dan nema snapshota, prikazan je zadnji raniji', 'No snapshot for the chosen day, showing the last earlier one')}: ` : `${t('Stanje', 'State')}: `}
                        {data.date}
                        {data.prev_date ? ` · ${t('usporedba s', 'compared with')} ${data.prev_date}` : ` · ${t('nema ranijeg dana za usporedbu', 'no earlier day to compare')}`}
                    </p>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{t('Redak', 'Row')}</th>
                                <th className="num">{t('Kom', 'Qty')}</th>
                                <th className="num">{t('Fine g', 'Fine g')}</th>
                                <th className="num">{t('Knjig. EUR', 'Book EUR')}</th>
                                <th className="num">{t('Spot EUR', 'Spot EUR')}</th>
                                <th className="num">{t('Δ fine g', 'Δ fine g')}</th>
                                <th className="num">{t('Δ spot EUR', 'Δ spot EUR')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r) => (
                                <tr key={r.key}>
                                    <td>{labels[r.key] ?? r.key.toUpperCase()}</td>
                                    <td className="num">{formatNumber(r.qty, locale)}</td>
                                    <td className="num">{formatNumber(r.fine_g, locale)}</td>
                                    <td className="num">{formatMoney(r.book_eur, locale)}</td>
                                    <td className="num">{formatMoney(r.spot_eur, locale)}</td>
                                    <td className="num">{delta(r.delta_fine_g, false)}</td>
                                    <td className="num">{delta(r.delta_spot_eur, true)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.rows.some((r) => r.definition_changed) ? (
                        <p className="muted">
                            {t(
                                'Između ova dva dana promijenjena je definicija izračuna; razlika nije samo promjena poslovanja.',
                                'The calculation definition changed between these two days; the difference is not only a business change.'
                            )}
                        </p>
                    ) : null}
                </>
            )}
        </div>
    );
}
